export interface GridLayerVisibilityInput {
  pixelSpacing1x: number;
  pixelSpacing10x?: number;
}

export interface GridLayerVisibility {
  showFine: boolean;
  showCoarse: boolean;
}

export interface GridMarginCellsInput {
  spanX: number;
  spanY: number;
  size: number;
  marginCells?: number;
}

export interface GridDragRecenterInput {
  renderedCenterX: number;
  renderedCenterY: number;
  currentCenterX: number;
  currentCenterY: number;
  spanX: number;
  spanY: number;
  isZooming?: boolean;
}

export interface GridZoomRefreshInput {
  previous: GridLayerVisibility | null;
  next: GridLayerVisibility;
}

export interface GridLayerRefreshOptions {
  refreshFine: boolean;
  refreshCoarse: boolean;
}

export interface GridLineRenderInput {
  lineIndex: number;
  majorGridVisible: boolean;
  majorLineInterval?: number;
}

const MIN_FINE_GRID_PIXEL_SPACING = 10;
const MIN_COARSE_GRID_PIXEL_SPACING = 30;
const MAX_COARSE_GRID_PIXEL_SPACING = 3500;
const MIN_GRID_MARGIN_CELLS = 8;
const GRID_MARGIN_VIEWPORT_RATIO = 4;
const GRID_DRAG_RECENTER_VIEWPORT_RATIO = 0.5;

export const getGridLayerVisibility = ({
  pixelSpacing1x,
  pixelSpacing10x = pixelSpacing1x * 10,
}: GridLayerVisibilityInput): GridLayerVisibility => {
  if (!Number.isFinite(pixelSpacing1x) || pixelSpacing1x <= 0) {
    return { showFine: false, showCoarse: false };
  }

  const showFine = pixelSpacing1x >= MIN_FINE_GRID_PIXEL_SPACING;
  const showCoarse =
    showFine ||
    (Number.isFinite(pixelSpacing10x) &&
      pixelSpacing10x >= MIN_COARSE_GRID_PIXEL_SPACING &&
      pixelSpacing10x <= MAX_COARSE_GRID_PIXEL_SPACING);

  return { showFine, showCoarse };
};

export const getGridMarginCells = ({
  spanX,
  spanY,
  size,
  marginCells = 1,
}: GridMarginCellsInput): number => {
  const requestedMargin = Number.isFinite(marginCells) ? Math.max(Math.ceil(marginCells), 0) : 0;
  if (!Number.isFinite(size) || size <= 0) {
    return Math.max(requestedMargin, MIN_GRID_MARGIN_CELLS);
  }

  const span = Math.max(Math.abs(spanX), Math.abs(spanY), 0);
  const viewportMarginCells = Math.ceil((span / size) * GRID_MARGIN_VIEWPORT_RATIO);

  return Math.max(requestedMargin, MIN_GRID_MARGIN_CELLS, viewportMarginCells);
};

const getViewportDriftRatio = (current: number, rendered: number, span: number): number => {
  if (!Number.isFinite(current) || !Number.isFinite(rendered) || !Number.isFinite(span)) {
    return 0;
  }
  const absoluteSpan = Math.abs(span);
  if (absoluteSpan <= 0) {
    return 0;
  }
  return Math.abs(current - rendered) / absoluteSpan;
};

export const shouldRecenterGridAfterDrag = ({
  renderedCenterX,
  renderedCenterY,
  currentCenterX,
  currentCenterY,
  spanX,
  spanY,
  isZooming = false,
}: GridDragRecenterInput): boolean => {
  if (isZooming) {
    return false;
  }

  const horizontalDrift = getViewportDriftRatio(currentCenterX, renderedCenterX, spanX);
  const verticalDrift = getViewportDriftRatio(currentCenterY, renderedCenterY, spanY);

  return (
    horizontalDrift >= GRID_DRAG_RECENTER_VIEWPORT_RATIO ||
    verticalDrift >= GRID_DRAG_RECENTER_VIEWPORT_RATIO
  );
};

export const shouldRefreshGridDuringZoom = ({
  previous,
  next,
}: GridZoomRefreshInput): boolean => {
  const nextVisible = next.showFine || next.showCoarse;
  if (!previous) {
    return nextVisible;
  }

  return previous.showFine !== next.showFine || previous.showCoarse !== next.showCoarse;
};

export const getGridZoomSourceRefreshPlan = ({
  previous,
  next,
}: GridZoomRefreshInput): GridLayerRefreshOptions => {
  if (!previous) {
    return {
      refreshFine: next.showFine,
      refreshCoarse: next.showCoarse,
    };
  }

  const fineVisibilityChanged = previous.showFine !== next.showFine;
  const coarseVisibilityChanged = previous.showCoarse !== next.showCoarse;

  return {
    refreshFine: fineVisibilityChanged,
    refreshCoarse: coarseVisibilityChanged || (next.showCoarse && fineVisibilityChanged),
  };
};

export const shouldRenderGridLine = ({
  lineIndex,
  majorGridVisible,
  majorLineInterval = 10,
}: GridLineRenderInput): boolean => {
  if (!majorGridVisible) {
    return true;
  }

  if (!Number.isFinite(lineIndex) || !Number.isFinite(majorLineInterval)) {
    return true;
  }

  const normalizedInterval = Math.abs(Math.trunc(majorLineInterval));
  if (normalizedInterval <= 1) {
    return true;
  }

  return lineIndex % normalizedInterval !== 0;
};
